import { AxiosError } from "axios";
import { getAuthItems } from "../../auth/AuthItems";
import { CONTENT_TYPE_JSON } from "../settings";
import { hideBusy, showBusy } from "../components/RsiUi";
import { isExpiredTokenResponse, isNoLicenseResponse } from "../../auth/RsiAuthService";
import type { TFunction } from "i18next";

/**
 * APIのHTTPヘッダーです。
 */
export interface ApiHttpHeaders extends Record<string, string> {
  /** 認証情報 */
  Authorization: string;
  /** コンテンツタイプ */
  "Content-Type": string;
}

/**
 * APIのレスポンスです。
 */
export interface ErrorResponseData {
  /** ステータスコード */
  status: number;
  /** 例外情報 */
  exception?: string;
  /** メッセージ */
  message?: string;
}

/**
 * APIのエラー結果です。
 */
export interface ApiErrorResult<T = unknown> {
  /** エラー情報 */
  error?: AxiosError<T>;
}

/**
 * APIの結果です。
 */
export interface ApiResult<T1, T2 = unknown> extends ApiErrorResult<T2> {
  data?: T1;
}

/**
 * API実行時のデフォルトヘッダを取得します。
 * @returns API実行時のデフォルトヘッダ
 */
export const getDefaultHeaders = (): ApiHttpHeaders | undefined => {
  const authItems = getAuthItems();
  const token = authItems.accessToken;

  return token
    ? {
        Authorization: "Bearer " + token,
        ...CONTENT_TYPE_JSON,
      }
    : undefined;
};

/**
 * API実行時のデフォルトヘッダを取得します。
 * @returns API実行時のデフォルトヘッダ
 */
export const getAdminHeaders = (): ApiHttpHeaders | undefined => {
  const authItems = getAuthItems();
  const token = authItems.accessToken;

  return token
    ? {
        Authorization: token,
        ...CONTENT_TYPE_JSON,
      }
    : undefined;
};

/**
 * APIを実行用のラッパ関数です。
 * 以下を実行します。
 * - 処理中のアニメーション表示
 * - 引数の func を実行
 * - 処理中のアニメーション表示を停止
 *
 * @param func - 実行関数。実際のAPI実行はここに記載する。
 */
export const callApi = async (func: () => void): Promise<void> => {
  const element = showBusy();
  await func();
  hideBusy(element);
};

export const handleErrorCommon = (
  data: ErrorResponseData | undefined,
  error: AxiosError,
  showBoundary: (error: Error) => void,
  t: TFunction,
): void => {
  const isExpired = isExpiredTokenResponse(data);
  if (isExpired) {
    alert(t("error.tokenExpiredMessage"));
    const authItems = getAuthItems();
    authItems.removeAccessToken();
    window.location.href = "/app";
    return;
  }

  const isNoLicense = isNoLicenseResponse(data);
  if (isNoLicense) {
    showBoundary(new Error(t("error.hasNoLicenseMessage")));
    return;
  }

  console.error(error);
  showBoundary(error);
};

export const handleErrorAdmin = (
  data: ErrorResponseData | undefined,
  error: AxiosError,
  showBoundary: (error: Error) => void,
  t: TFunction,
): void => {
  const isExpired = isExpiredTokenResponse(data);
  if (isExpired) {
    alert(t("error.tokenExpiredMessage"));
    const authItems = getAuthItems();
    authItems.removeAccessToken();
    window.location.href = "/admin/login";
    return;
  }

  const isDuplicatedMailAddress =
    data?.status === 400 &&
    data.exception === "org.springframework.web.server.ResponseStatusException" &&
    data.message === "INVALID_CUSTOMER_INFO";
  if (isDuplicatedMailAddress) {
    alert(t("error.duplicatedMailAddress"));
    return;
  }

  console.error(error);
  showBoundary(error);
};
